import { Link } from 'react-router-dom'
import clsx from 'clsx'

import { minutesToClock, weekdayName, formatShortDate, type ISODate } from '@app/shared'
import {
  useAcceptChatProposal,
  useChatAttachmentUrl,
  useRejectChatProposal,
  type ChatProposal,
  type ChatProposalEvidence,
  type ChatProposalResult,
} from '../../lib/data'
import { AlertCircle } from './icons'
import { recurrenceWord } from '../tasks/derive'

// ============================================================================
// A PROPOSAL CARD — Chat v3.
//
// THE PRODUCT RULE THIS COMPONENT IS: **propose, never silently do.** The answer above it may
// have read a Calendly confirmation off a screenshot; this card is where that reading is SHOWN
// to the owner before anything is written. Accept creates the block. Reject writes nothing. There
// is no third path and there is nothing that happens on its own.
//
// A CARD IS NEVER RENDERED WITHOUT ITS EVIDENCE LINE. That is enforced here by returning null
// rather than by a comment: a proposal that cannot say where it came from is exactly the
// fabricated knowledge the whole screen exists to refuse, and rendering it "just without the
// line" would be the app quietly dropping the one part that makes it checkable. The data layer
// filters these too (remote.ts) — this is the second belt, in the place a reader can see it.
//
// EVERY TIME IS ALREADY FORMATTED, OR FORMATTED HERE FROM MINUTES. Nothing on this card parses an
// instant into a wall clock with browser-local arithmetic: the Worker sends `whenLabel` on a
// conflict already in the owner's zone, and the payload's own times carry their offset. 12-hour,
// via @app/shared, like every other clock in the app.
// ============================================================================

export function ProposalCard({
  proposal,
  threadId,
}: {
  proposal: ChatProposal
  /** Which thread to re-read after a decision. Undefined only in a preview with no thread. */
  threadId: string | undefined
}) {
  const accept = useAcceptChatProposal(threadId)
  const reject = useRejectChatProposal(threadId)

  // THE INVARIANT, ENFORCED. See the header: no evidence, no card.
  if (proposal.evidence.length === 0) return null

  const isTask = proposal.kind === 'task'
  const decided = proposal.status !== 'proposed'
  const busy = accept.isPending || reject.isPending
  const error = accept.error ?? reject.error

  return (
    <div
      className={clsx(
        'flex max-w-[520px] flex-col gap-[10px] rounded-card border bg-surface px-[15px] py-[13px]',
        proposal.status === 'rejected'
          ? 'border-dashed border-dash opacity-60'
          : 'border-line-2 shadow-composer',
      )}
    >
      <div className="flex items-center gap-[7px]">
        <span
          className={clsx(
            'rounded-pill px-[7px] py-[2px] text-[9px] font-semibold leading-[1.6] tracking-[0.04em]',
            isTask ? 'bg-orange-fill text-orange' : 'bg-accent-fill text-accent',
          )}
        >
          {isTask ? 'ADD REMINDER?' : 'ADD TO CALENDAR?'}
        </span>
        {decided && (
          <span className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.06em] text-microcopy">
            {proposal.status === 'accepted' ? 'added' : 'not added'}
          </span>
        )}
      </div>

      <p className="text-[13px] font-semibold leading-[1.35] text-ink text-pretty">
        {proposal.payload.title}
      </p>

      <p className="text-[11.5px] font-normal leading-[1.45] text-ink-2">{whenLine(proposal)}</p>

      {proposal.payload.kind === 'calendar_block' && proposal.payload.location && (
        <p className="text-[11px] font-normal leading-[1.4] text-ink-3">
          {proposal.payload.location}
        </p>
      )}

      {proposal.payload.notes && (
        <p className="text-[11px] font-normal leading-[1.45] text-ink-3 text-pretty">
          {proposal.payload.notes}
        </p>
      )}

      <EvidenceLine evidence={proposal.evidence} quote={proposal.payload.quote} />

      {/* The amber strip, the same one the scope-limit warning wears. A clash is a fact about the
          week, stated once, with no advice attached — this app is a mirror, not an adviser, and
          "you might want to move it" is not its sentence to write. */}
      {proposal.conflicts.length > 0 && (
        <div className="flex items-start gap-[9px] rounded-row border border-warning-line bg-warning-fill px-[11px] py-[8px]">
          <AlertCircle className="mt-[1px] flex-none text-warning-2" />
          <p className="text-[11px] font-normal leading-[1.45] text-warning text-pretty">
            Overlaps:{' '}
            {proposal.conflicts.map((c) => `${c.title} ${c.whenLabel}`).join(' · ')}
          </p>
        </div>
      )}

      {proposal.status === 'proposed' ? (
        <div className="flex items-center gap-[7px]">
          <button
            type="button"
            disabled={busy}
            onClick={() => accept.mutate(proposal.id)}
            className={clsx(
              'mo-press rounded-button px-[13px] py-[6px] text-[11px] font-semibold leading-none',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              busy ? 'bg-fill text-microcopy' : 'bg-ink text-chat-lift hover:bg-ink-2',
            )}
          >
            Accept
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => reject.mutate(proposal.id)}
            className={clsx(
              'mo-press rounded-button border border-line px-[13px] py-[6px] text-[11px] font-medium leading-none text-ink-3',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2',
            )}
          >
            Reject
          </button>
        </div>
      ) : (
        <Decided proposal={proposal} created={accept.data?.created} isTask={isTask} />
      )}

      {/* A REFUSAL IS PRINTED, never swallowed. The one that matters is the second Accept: the
          Worker answers 409 with a sentence saying it was already added, and showing that is how
          a double-tap reads as "already done" instead of as a button that did nothing. */}
      {error != null && (
        <p role="status" className="text-[11px] leading-[1.45] text-danger text-pretty">
          {error instanceof Error ? error.message : 'That did not go through, and gave no reason.'}
        </p>
      )}
    </div>
  )
}

/** 'Thu Sep 3 · 1:00 PM – 3:00 PM' for a block; the day and any nudge for a reminder. */
function whenLine(proposal: ChatProposal): string {
  if (proposal.payload.kind === 'calendar_block') {
    const start = proposal.payload.startsAt
    const end = proposal.payload.endsAt
    return `${dayLabel(start)} · ${clockOf(start)} – ${clockOf(end)}`
  }
  const { due, reminderAt, rrule } = proposal.payload
  // THE REPEAT, SAID THE WAY IT WORKS: the next one is written when this one is completed, not on
  // a timer (tasks/presets.ts → repeatHint carries the same sentence on the Reminders page).
  const word = recurrenceWord(rrule ?? null)
  const repeat = word ? ` · repeats ${word} after you complete it` : ''
  // NOTHING IS FILLED IN. A reminder with a day and no time says so; one with neither says that.
  // "11:59 PM" is the invented hour this whole codebase refuses to write.
  if (due && reminderAt) return `${dayLabel(`${due}T00:00:00`)} · ${clockOf(reminderAt)}${repeat}`
  if (due) return `${dayLabel(`${due}T00:00:00`)} · no time given${repeat}`
  if (reminderAt) return `${dayLabel(reminderAt)} · ${clockOf(reminderAt)}${repeat}`
  return 'No day — it would sit in the list until you give it one'
}

/**
 * The wall clock the timestamp itself states.
 *
 * Read off the STRING and not off a `Date`, deliberately: the Worker sends an instant with the
 * owner's own offset on it, and `new Date(...).getHours()` would re-render it in whatever zone
 * the laptop happens to be in — which is how a 1pm appointment becomes 4pm on a trip.
 */
const clockOf = (iso: string): string =>
  minutesToClock(Number(iso.slice(11, 13)) * 60 + Number(iso.slice(14, 16)))

const dayLabel = (iso: string): string => {
  const date = iso.slice(0, 10) as ISODate
  return `${weekdayName(date, true)} ${formatShortDate(date)}`
}

/**
 * WHOSE VOICE A CITED TRANSCRIPT PASSAGE WAS — as a likelihood, which is all it ever is.
 *
 * The desktop worker labels the dominant voice by total talk time `professor` and everyone else
 * `speaker-2`, `speaker-3`, … It has no recording of any professor to compare against, so the
 * word is a guess and is never shown bare: "(likely)" travels with it, and another voice is named
 * as NOT the professor so a date read off a student's aside is visibly that. Null for a lecture
 * nobody diarized — and then nothing is drawn, because "unknown speaker" is a claim about a
 * recording that was never analysed.
 */
function speakerNote(speaker: string | null | undefined): string | null {
  const label = speaker?.trim()
  if (!label) return null
  return label === 'professor' ? 'professor (likely)' : `${label} (not the professor)`
}

/**
 * WHERE THIS CAME FROM — the line that makes the card checkable rather than persuasive.
 *
 * A source entry reuses the answer's own numbering ("[3] COGSCI 175 · Syllabus") and links to the
 * same route its card does, plus whose voice it was when the recording was diarized. An
 * attachment entry shows the picture itself. A `request` entry says "You asked" and prints his own
 * sentence back: the Worker verified that quote is really in the message he sent, so this is the
 * one evidence kind whose whole content the owner already knows to be true.
 *
 * Under them sits the literal quote the proposal was read out of — EXCEPT when his own ask is the
 * only evidence there is, because the line above has already printed it and saying the same words
 * twice reads as two pieces of evidence for one.
 */
function EvidenceLine({
  evidence,
  quote,
}: {
  evidence: ChatProposalEvidence[]
  quote: string
}) {
  const onlyHisAsk = evidence.every((item) => item.kind === 'request')
  return (
    <div className="flex flex-col gap-[7px] rounded-row bg-surface-2 px-[11px] py-[9px]">
      <div className="flex flex-wrap items-center gap-[9px]">
        {evidence.map((item, i) => {
          if (item.kind === 'request') {
            return (
              <span
                key={`r-${i}`}
                className="text-[10.5px] font-medium leading-[1.35] text-ink-3 text-pretty"
              >
                You asked: “{item.quote}”
              </span>
            )
          }
          if (item.kind === 'source') {
            const voice = speakerNote(item.speaker)
            return (
              <span key={`s-${item.cite}-${i}`} className="flex flex-wrap items-center gap-[5px]">
                <Link
                  to={item.source.href}
                  className="text-[10.5px] font-medium leading-[1.35] text-ink-3 underline-offset-2 hover:underline"
                >
                  [{item.cite}] {item.source.locator}
                </Link>
                {voice && (
                  <span className="text-[10.5px] font-normal leading-[1.35] text-microcopy">
                    · {voice}
                  </span>
                )}
              </span>
            )
          }
          return (
            <span key={`a-${item.attachmentId}`} className="flex items-center gap-[6px]">
              <EvidenceThumb attachmentId={item.attachmentId} />
              <span className="text-[10.5px] font-medium leading-[1.35] text-ink-3">
                From your screenshot
              </span>
            </span>
          )
        })}
      </div>
      {!onlyHisAsk && (
        <p className="text-[10.5px] font-normal italic leading-[1.5] text-microcopy text-pretty">
          “{quote}”
        </p>
      )}
    </div>
  )
}

/** The 34px picture beside "From your screenshot". A load failure says so rather than a blank. */
function EvidenceThumb({ attachmentId }: { attachmentId: string }) {
  const { data: url, error } = useChatAttachmentUrl(attachmentId)
  if (error || !url) {
    return (
      <span className="flex size-[34px] flex-none items-center justify-center rounded-chip border border-dashed border-dash text-[8px] leading-none text-microcopy">
        {error ? '?' : ''}
      </span>
    )
  }
  return (
    <img
      src={url}
      alt="The picture this was read from"
      className="size-[34px] flex-none rounded-chip border border-line object-cover"
    />
  )
}

/**
 * AFTER THE DECISION. Accepting says what now exists AND links to it — a confirmation the owner
 * cannot open is one he has to take on trust, which is the thing this feature is trying to avoid.
 *
 * `created` is only in hand on the render right after the press; on a reload the card is still
 * `accepted` and still says so, without the link, because the id it would link to is on the row
 * and not on the proposal. Saying less rather than guessing a route is the honest half.
 */
function Decided({
  proposal,
  created,
  isTask,
}: {
  proposal: ChatProposal
  created: ChatProposalResult | undefined
  isTask: boolean
}) {
  if (proposal.status === 'rejected') {
    return (
      <p className="text-[11px] leading-[1.45] text-microcopy">
        You said no to this one. Nothing was created.
      </p>
    )
  }
  const label = isTask ? 'Added to Reminders' : 'Added to Calendar'
  return (
    <p className="text-[11px] font-medium leading-[1.45] text-success-dot">
      {created ? (
        <Link to={created.href} className="underline underline-offset-2">
          {label}
        </Link>
      ) : (
        label
      )}
    </p>
  )
}
